import React from 'react';
import HubPageLayout from '../components/HubPageLayout';
import FramedPanel from '../components/FramedPanel';
import SplitSection from '../components/SplitSection';

// The Day After — post-scam recovery content. Copy from the curated QA doc.

const MILESTONES = [
  'Know that you are not alone — everyone is a target.',
  'Recognize and understand the emotional scars being victimized has caused.',
  'Understand the scam does not define your value or self-worth.',
  'Understand how dark psychological manipulation works.',
  'Consciously practice self-compassion.',
  'Grieve what was lost.',
  'Retake your personal control — trust your judgement, become informed, aware, knowledgeable.',
  'Accept human vulnerability.',
  'Accept your right to move forward.',
  'Embrace, love and cherish family and lifelong emotionally healthy relationships.',
];

const TheDayAfterPage = () => (
  <HubPageLayout>
    <FramedPanel title="The Day After">
      <div className="px-6 pt-8 pb-6">
        <img
          src="/images/mosaic-psyops/the-day-after-hero.jpeg"
          alt=""
          className="w-full h-[300px] object-cover"
        />
      </div>
    </FramedPanel>

    <SplitSection
      heading="Broke hearts & an empty bank account"
      className="mt-10"
      paragraphs={[
        'All scams have an end date — the date on which the illusion collides with reality and the victim realizes the unvarnished truth. On that date you’ll be shaken to your core, in a state of shock, dazed and confused, unsure of which end is up.',
        'What is real? Everything was fake. This has all been a cruel and heartless lie. It was never about you — it was always about your money. Victims turn to blaming themselves for being abused: you should have known better, how could you have not seen this coming.',
        'Scammers target all ages, sexes, education and income levels. They create high-gloss social media profiles to build intrigue and lower your emotional defenses. When that illusion collapses, the damage is more than the money — it robs you of your self-worth, sense of judgment and confidence, and leaves you financially devastated with your identity in shambles.',
      ]}
    />

    <SplitSection
      heading="Coming to grips with the pain"
      paragraphs={[
        'Personal forgiveness is not easy. Scams are soul crushing. It’s a very long road, a private journey, and it does not happen overnight. You’ll need to give yourself time for self-reflection. The road to recovery is filled with good and bad news.',
        'The bad news first — the emotional impact runs deep. The good news is that you can recover what was stolen: your confidence, peace of mind and self-worth.',
        'The road ahead is filled with challenges that will test you, peeling back different emotional layers and reliving painful and embarrassing feelings and experiences. Emotional recovery is not for the faint of heart, and it’s not a one-size-fits-all process that leads to reclaiming your personal freedom and peace overnight.',
        'Recovering from being scammed is hard. You’ll have ups and downs, good days and bad ones, and they may come in waves — that’s normal. While healing from a trust-based scam does not follow a straight line, there are key milestones that take place.',
      ]}
    >
      <ol className="mt-2 space-y-3">
        {MILESTONES.map((milestone, i) => (
          <li key={milestone} className="flex gap-4">
            <span className="text-blue-600 font-bold tabular-nums flex-shrink-0">
              {String(i + 1).padStart(2, '0')} /
            </span>
            <span className="text-black leading-relaxed">{milestone}</span>
          </li>
        ))}
      </ol>
    </SplitSection>

    <SplitSection
      heading="The long road ahead"
      paragraphs={[
        'While the recovery journey has common touchpoints for victims, the road is open to all — and victims must travel it themselves. It is a painstaking, private affair.',
        'It’s important to seek out professionals to help guide you to reclaim what was stolen. Don’t make this harder than it already is. Seek out trusted people, join a support group, or work with a qualified therapist to ease the burden. It becomes easier when you surround yourself with people on your side who love and value you for being you.',
        'Being the victim of a scam is a life event. While painful, it does not have to define you. You can forgive yourself and start anew — wiser. That choice belongs to you, and every step you take toward that goal strengthens your recovery.',
        'Being victimized at the hands of a scammer is a gut punch — deep emotional gashes that strip you of humanity and pale in comparison to the financial losses. You will ride an emotional roller coaster. Recognize that these feelings are valid and part of the healing process. Give yourself permission to grieve and process what has happened. You don’t have to have everything figured out right away.',
      ]}
    />

    <h2 className="text-2xl xl:text-3xl font-bold text-black mt-12 mb-6">
      Know there is a better tomorrow
    </h2>
    <img
      src="/images/mosaic-psyops/the-day-after-tomorrow.jpeg"
      alt=""
      className="w-full max-h-[380px] object-cover"
    />
    <p className="text-black leading-relaxed mt-6">
      Finally, remember that many people have faced similar challenges and found ways to cope. Lean
      on your healthcare team, loved ones and broader support networks. You’re not alone, and there
      is strength in seeking help when you need it. Remember to prioritize your emotional and
      physical well-being as you move forward, one step at a time.
    </p>
  </HubPageLayout>
);

export default TheDayAfterPage;
